import { ERROR_CODES } from './apiClient';

// ============================================================================
// Error Type Checks
// ============================================================================

export function isUnauthorized(error) {
  if (!error) return false;
  return (
    error.status === 401 ||
    error.code === ERROR_CODES.UNAUTHORIZED ||
    error.code === ERROR_CODES.SESSION_EXPIRED ||
    error.code === ERROR_CODES.SESSION_INVALID
  );
}

export function isForbidden(error) {
  if (!error) return false;
  return (
    error.status === 403 || error.code === ERROR_CODES.ACCESS_DENIED
  );
}

export function isNotFound(error) {
  if (!error) return false;
  return (
    error.status === 404 || error.code === ERROR_CODES.NOT_FOUND
  );
}

export function isNetworkError(error) {
  if (!error) return false;
  return (
    error.status === 0 || error.code === ERROR_CODES.NETWORK_ERROR
  );
}

export function isServerError(error) {
  if (!error) return false;
  return (
    error.status >= 500 || error.code === ERROR_CODES.SERVER_ERROR
  );
}

// ============================================================================
// Error Message Generation
// ============================================================================

export function getErrorMessage(error) {
  if (!error) {
    return 'An unknown error occurred';
  }

  // Use provided message if available
  if (error.message) {
    return error.message;
  }

  // Generate from code
  switch (error.code) {
    case ERROR_CODES.UNAUTHORIZED:
      return 'Your session has expired. Please log in again.';
    case ERROR_CODES.SESSION_EXPIRED:
      return 'Session expired. Please log in again.';
    case ERROR_CODES.SESSION_INVALID:
      return 'Your session is invalid. Please log in again.';
    case ERROR_CODES.ACCESS_DENIED:
      return 'You do not have permission to access this resource.';
    case ERROR_CODES.NOT_FOUND:
      return 'The requested resource was not found.';
    case ERROR_CODES.NETWORK_ERROR:
      return 'Network error. Please check your connection and try again.';
    case ERROR_CODES.SERVER_ERROR:
      return 'Server error. Please try again later.';
    case ERROR_CODES.INVALID_RESPONSE:
      return 'Invalid response from server.';
    default:
      return error.details?.error || 'An error occurred';
  }
}

// ============================================================================
// Error Details Extraction
// ============================================================================

export function getErrorDetails(error) {
  if (!error) {
    return null;
  }

  return {
    code: error.code,
    message: error.message || getErrorMessage(error),
    status: error.status,
    details: error.details || null,
  };
}

// ============================================================================
// API Response Handling Helper
// ============================================================================

export function handleApiResponse({ data, error, status }) {
  if (error) {
    return {
      success: false,
      data: null,
      error: getErrorDetails(error),
      isUnauthorized: isUnauthorized(error),
      isForbidden: isForbidden(error),
      isNotFound: isNotFound(error),
      isNetworkError: isNetworkError(error),
      isServerError: isServerError(error),
    };
  }

  return {
    success: true,
    data,
    error: null,
    isUnauthorized: false,
    isForbidden: false,
    isNotFound: false,
    isNetworkError: false,
    isServerError: false,
  };
}
